import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PersonOutlineIcon from "@mui/icons-material/PersonOutlined";
import SettingsIcon from "@mui/icons-material/Settings";
import { Box, Chip, CircularProgress, IconButton, Paper, Tooltip, Typography } from "@mui/material";
import { styled } from "@mui/system";
import { DateTime } from "luxon";
import { type Dispatch, type FC, Fragment, type MouseEvent, type SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useDynamicRowHeight } from "react-window";
import { PushReportSchedule } from "../../components/PushReportSchedule/PushReportSchedule";
import RoleSelector from "../../components/rolesSelector/rolesSelector";
import { TEAM_SECTIONS } from "../../components/team/config";
import { SystemFlatSwitch } from "../../components/UIToolkit/Clickable/SystemFlatSwitch";
import { StaffAutoComplete } from "../../components/UIToolkit/Inputs/StaffAutoComplete";
import type { SystemAutoCompleteOption } from "../../components/UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import { SystemDropdown } from "../../components/UIToolkit/Inputs/SystemDropDown";
import { DateRangeSelector } from "../../components/UIToolkit/molecules/DateRangeSelector";
import { VirtualizedList } from "../../components/UIToolkit/molecules/VirtualizedList";
import type { MultiSortOption } from "../../components/UIToolkit/Popups/SystemSortingButtonWithPopup/SystemSortingButtonWithPopup";
import { SystemSortingButtonWithPopup } from "../../components/UIToolkit/Popups/SystemSortingButtonWithPopup/SystemSortingButtonWithPopup";
import { useAuth } from "../../contexts/AuthProvider";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { useDepartmentFlags } from "../../hooks/useDepartmentFlags";
import { useDepartmentNurseTypes } from "../../hooks/useDepartmentNurseTypes";
import { useMultiSort } from "../../hooks/useMultiSort";
import { useRosterNurse } from "../../hooks/useNurses";
import { useRoleColors } from "../../hooks/useRoleColors";
import { useRosterLookup } from "../../hooks/useRosterLookup";
import { disciplinaryReportTableSortingKey, localStorageService } from "../../services/LocalStorageService";
import { DISCIPLINARY_VIOLATION } from "../../theme/tokens";
import { formatWeekRangeWithMonth } from "../../utils/dateFormatting";
import {
	type DisciplinaryReportRowData,
	type DisciplinaryViolation,
	DisciplinaryViolationType,
	type EmploymentType,
	MissingPunchSide,
	type Nurse,
	type NurseFullRole,
	PushReportType,
} from "../../utils/models";
import { handleColumnSortClick, type SortingState } from "../../utils/multiColumnSorting";
import { DisciplinaryExportButton } from "./DisciplinaryExportButton";
import { DisciplinarySettingsDialog } from "./DisciplinarySettingsDialog";
import { DisciplinarySummaryCards } from "./DisciplinarySummaryCards";
import { EditDisciplinaryNote } from "./EditDisciplinaryNote";
import { useGetDisciplinaryReport } from "./useDisciplinaryReport";
import { VIOLATION_TYPE_LABEL_KEYS, violationLabelKey } from "./violationLabels";

const gridTemplateColumns = "40px 1fr 120px 180px 210px";

const ALL_VIOLATION_TYPES = Object.values(DisciplinaryViolationType);

type ChipColor = { bg: string; text: string };

const VIOLATION_CHIP_COLORS = {
	[DisciplinaryViolationType.NCNS]: { bg: DISCIPLINARY_VIOLATION.ABSENCE_BG, text: DISCIPLINARY_VIOLATION.ABSENCE_INK },
	[DisciplinaryViolationType.CallIn]: { bg: DISCIPLINARY_VIOLATION.ABSENCE_BG, text: DISCIPLINARY_VIOLATION.ABSENCE_INK },
	[DisciplinaryViolationType.LateArrival]: { bg: DISCIPLINARY_VIOLATION.TIMING_BG, text: DISCIPLINARY_VIOLATION.TIMING_INK },
	[DisciplinaryViolationType.HomeEarly]: { bg: DISCIPLINARY_VIOLATION.TIMING_BG, text: DISCIPLINARY_VIOLATION.TIMING_INK },
	[DisciplinaryViolationType.ShiftDelta]: { bg: DISCIPLINARY_VIOLATION.DELTA_BG, text: "primary.dark" },
	[DisciplinaryViolationType.SkippedLunch]: { bg: DISCIPLINARY_VIOLATION.LUNCH_BG, text: DISCIPLINARY_VIOLATION.LUNCH_INK },
	[DisciplinaryViolationType.MissingPunch]: { bg: DISCIPLINARY_VIOLATION.PUNCH_IN_BG, text: DISCIPLINARY_VIOLATION.PUNCH_IN_INK },
} as const satisfies Record<DisciplinaryViolationType, ChipColor>;

// The two halves of a punch pair read as distinct events, so they get distinct chips.
const MISSING_PUNCH_SIDE_CHIP_COLORS = {
	[MissingPunchSide.In]: { bg: DISCIPLINARY_VIOLATION.PUNCH_IN_BG, text: DISCIPLINARY_VIOLATION.PUNCH_IN_INK },
	[MissingPunchSide.Out]: { bg: DISCIPLINARY_VIOLATION.PUNCH_OUT_BG, text: DISCIPLINARY_VIOLATION.PUNCH_OUT_INK },
} as const satisfies Record<MissingPunchSide, ChipColor>;

const violationChipColor = (violation: DisciplinaryViolation): ChipColor =>
	violation.violationType === DisciplinaryViolationType.MissingPunch && violation.missingPunchSide
		? MISSING_PUNCH_SIDE_CHIP_COLORS[violation.missingPunchSide]
		: VIOLATION_CHIP_COLORS[violation.violationType];

const EM_DASH = "—";

/** The worked window, with an em-dash standing in for the half a missing punch never recorded. Null when nothing was punched at all. */
const formatActualWindow = ({ actualStart, actualEnd }: DisciplinaryViolation): string | null =>
	!actualStart && !actualEnd ? null : `${actualStart ? actualStart.toFormat("h:mm a") : EM_DASH} - ${actualEnd ? actualEnd.toFormat("h:mm a") : EM_DASH}`;

const StyledRow = styled(Box)<{ hasDetails: boolean }>(({ hasDetails, theme }) => ({
	backgroundColor: "transparent",
	width: "100%",
	display: "grid",
	gridTemplateColumns: gridTemplateColumns,
	gap: theme.spacing(2),
	padding: theme.spacing(2),
	border: "none",
	borderBottom: "1px solid #E0E0E0",
	alignItems: "center",
	cursor: hasDetails ? "pointer" : "default",
	"&:hover": { backgroundColor: hasDetails ? "#F5F5F5" : "transparent" },
}));

const dateTypographyWidth = 170;

const DisciplinaryReportRow: FC<{
	data: DisciplinaryReportRowData;
	isExpanded: boolean;
	onToggleExpand: () => void;
	dateSortOrder: "asc" | "desc";
}> = ({ data, isExpanded, onToggleExpand, dateSortOrder }) => {
	const roleColor = useRoleColors();
	const { chosenDepartment } = useChosenDepartment();
	const nurse = useRosterNurse(chosenDepartment.department_id, data.nurseId);
	const navigate = useNavigate();
	const location = useLocation();
	const { track } = useAnalytics();
	const { t } = useTranslation();

	const hasDetails = data.violations.length > 0;

	const sortedViolations = data.violations.slice().sort((v1, v2) => {
		const dateA = DateTime.fromISO(v1.date).toMillis();
		const dateB = DateTime.fromISO(v2.date).toMillis();
		return dateSortOrder === "asc" ? dateA - dateB : dateB - dateA;
	});
	const callInsCount = data.violations.filter(
		(v) => v.violationType === DisciplinaryViolationType.NCNS || v.violationType === DisciplinaryViolationType.CallIn,
	).length;
	const scheduleViolationsCount = data.violations.filter(
		(v) => v.violationType !== DisciplinaryViolationType.NCNS && v.violationType !== DisciplinaryViolationType.CallIn,
	).length;

	return (
		<Box>
			{/* Main Row */}
			<StyledRow hasDetails={hasDetails} onClick={hasDetails ? onToggleExpand : undefined}>
				{/* Chevron */}
				<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
					{hasDetails && (
						<ChevronRightIcon
							sx={{ color: "primary.dark", fontSize: 20, transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.2s ease" }}
						/>
					)}
				</Box>

				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1,
					}}
				>
					{nurse && (
						<Box
							sx={{
								width: 8,
								height: 8,
								borderRadius: "50%",
								backgroundColor: roleColor(nurse.role).dot,
							}}
						/>
					)}
					<Typography variant="body2" sx={{ color: "primary.dark", fontWeight: 500 }}>
						{data.nurseName}
					</Typography>
					<IconButton
						size="small"
						onClick={(e) => {
							e.stopPropagation();
							track("disciplinary_report_profile_clicked", { nurse_id: data.nurseId });
							navigate(`/${chosenDepartment.department_id}/nurse/${data.nurseId}`, {
								state: { navigationSrc: location.pathname },
							});
						}}
						sx={{ color: "primary.dark" }}
					>
						<PersonOutlineIcon sx={{ fontSize: 20 }} />
					</IconButton>
				</Box>
				<Typography
					variant="body2"
					sx={{
						color: "primary.dark",
						fontWeight: callInsCount > 0 ? 600 : 400,
						textAlign: "center",
					}}
				>
					{callInsCount}
				</Typography>
				<Typography
					variant="body2"
					sx={{
						color: "primary.dark",
						fontWeight: scheduleViolationsCount > 0 ? 600 : 400,
						textAlign: "center",
					}}
				>
					{scheduleViolationsCount}
				</Typography>
				<Typography
					variant="body2"
					sx={{
						color: data.score > 0 ? "#D32F2F" : "primary.dark",
						fontWeight: data.score > 0 ? 600 : 400,
						textAlign: "center",
					}}
				>
					{data.score}
				</Typography>
			</StyledRow>
			{/* Expanded Details - Violation Breakdown */}
			{isExpanded && hasDetails && (
				<Box component="ul" sx={{ px: 3, py: 2, mt: 0, backgroundColor: "#FAFBFC", borderBottom: "1px solid #E0E0E0", listStyle: "none" }}>
					<Typography component="li" variant="subtitle2" sx={{ color: "primary.dark", fontWeight: 700, mb: 1 }}>
						Violation Breakdown ({sortedViolations.length})
					</Typography>

					{sortedViolations.map((violation, idx) => {
						const dateObj = DateTime.fromISO(violation.date);
						const dayName = dateObj.toFormat("ccc");
						const actualWindow = formatActualWindow(violation);
						return (
							<Box
								key={`${violation.shiftId}-${idx}`}
								component="li"
								sx={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
									gap: 2,
									py: 1,
									borderBottom: idx < sortedViolations.length - 1 ? "1px solid #E0E0E0" : "none",
								}}
							>
								{/* Date */}
								<Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: dateTypographyWidth }}>
									<Typography variant="body2" sx={{ color: "#666" }}>
										{dateObj.toFormat("dd MMM yyyy")}
									</Typography>
									<Chip
										label={dayName}
										size="small"
										sx={{
											height: 20,
											fontSize: 11,
											fontWeight: 600,
											backgroundColor: violation.isWeekend ? "#BBDEFB" : "#F5F5F5",
											color: violation.isWeekend ? "#1565C0" : "#666",
										}}
									/>
								</Box>

								{/* Violation Type */}
								<Chip
									label={t(violationLabelKey(violation))}
									size="small"
									sx={{
										backgroundColor: violationChipColor(violation).bg,
										color: violationChipColor(violation).text,
										fontWeight: 600,
										minWidth: 110,
									}}
								/>

								{/* Shift Times */}
								<Box sx={{ minWidth: 200 }}>
									<Typography variant="body2" sx={{ color: "#666", fontSize: 12 }}>
										Sched: {violation.plannedStart.toFormat("h:mm a")} - {violation.plannedEnd.toFormat("h:mm a")}
									</Typography>
									{actualWindow ? (
										<Typography variant="body2" sx={{ color: "#666", fontSize: 12 }}>
											Actual: {actualWindow}
										</Typography>
									) : (
										<Typography variant="body2" sx={{ color: "#D32F2F", fontSize: 12, fontWeight: 600 }}>
											Actual: Did not show
										</Typography>
									)}
								</Box>

								{/* Details */}
								<Typography variant="body2" sx={{ color: "#666", minWidth: 130 }}>
									{violation.details}
								</Typography>

								{/* Notes */}
								<Box sx={{ flex: 1, minWidth: 180, maxWidth: 280 }} onClick={(e) => e.stopPropagation()}>
									<EditDisciplinaryNote violation={violation} />
								</Box>

								{/* Points */}
								<Typography
									variant="body2"
									sx={{
										color: "#D32F2F",
										fontWeight: 600,
										minWidth: 50,
										textAlign: "right",
									}}
								>
									{violation.points} pts
								</Typography>
							</Box>
						);
					})}
				</Box>
			)}
		</Box>
	);
};

const sortingOptions: MultiSortOption<DisciplinaryReportRowData>[] = [
	{ label: "Name", sortColumns: ["nurseName"] },
	{ label: "Violations", sortColumns: ["violationCount"] },
	{ label: "Disciplinary Score", sortColumns: ["score"] },
];

const initialSortState: SortingState<DisciplinaryReportRowData>[] = [{ sortColumns: ["score"], sortOrder: "desc", label: "Disciplinary Score" }];
const SortButton = ({
	sortingColumnsKeys,
	setSortState,
	sortingState,
}: {
	sortingColumnsKeys: Array<keyof DisciplinaryReportRowData>;
	setSortState: Dispatch<SetStateAction<SortingState<DisciplinaryReportRowData>[]>>;
	sortingState: SortingState<DisciplinaryReportRowData>[];
}) => {
	const foundSortingState = sortingState.find((sState) => sState.sortColumns.every((col) => sortingColumnsKeys.includes(col)));
	const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		handleColumnSortClick({
			label: sortingOptions.find((opt) => opt.sortColumns?.every((col) => sortingColumnsKeys.includes(col)))?.label || "",
			columns: sortingColumnsKeys,
			sortState: sortingState,
			setSortState: setSortState,
			appendToStartIfAlreadyExists: true,
			allowRemovalWhenDescendingToggle: false,
		});
	};
	return (
		<IconButton size="small" onClick={handleClick}>
			{foundSortingState?.sortOrder === "asc" ? (
				<ArrowUpwardIcon fontSize="inherit" />
			) : foundSortingState?.sortOrder === "desc" ? (
				<ArrowUpwardIcon fontSize="inherit" style={{ transform: "rotate(180deg)" }} />
			) : (
				<ArrowUpwardIcon fontSize="inherit" style={{ opacity: 0.4 }} />
			)}
		</IconButton>
	);
};

const filtersWidth = 180;

const parseDateParam = (searchParams: URLSearchParams, key: string, timezone: string, fallback: DateTime): DateTime => {
	const param = searchParams.get(key);
	if (param) {
		const parsed = DateTime.fromISO(param, { zone: timezone });
		if (parsed.isValid) return parsed;
	}
	return fallback;
};

const DisciplinaryReport = () => {
	const { t } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	const { isAnyDemo } = useDepartmentFlags();
	const departmentNurseTypes = useDepartmentNurseTypes(chosenDepartment.department_id);
	const { roster, getNurseFormattedName, isLoading: isLoadingRoster, isError: isRosterError } = useRosterLookup(chosenDepartment.department_id);
	const {
		userInfo: { id: userId },
	} = useAuth();
	const [searchParams, setSearchParams] = useSearchParams();
	const currentDate = useMemo(() => DateTime.now(), []);
	const yesterday = useMemo(() => currentDate.minus({ days: 1 }), [currentDate]);
	const [startDate, setStartDate] = useState<DateTime>(() =>
		parseDateParam(searchParams, "startDate", chosenDepartment.timezone, yesterday.minus({ days: 30 })),
	);
	const [endDate, setEndDate] = useState<DateTime>(() => parseDateParam(searchParams, "endDate", chosenDepartment.timezone, yesterday));

	useEffect(() => {
		setSearchParams(
			(prev) => {
				prev.set("startDate", startDate.toFormat("yyyy-MM-dd"));
				prev.set("endDate", endDate.toFormat("yyyy-MM-dd"));
				return prev;
			},
			{ replace: true },
		);
	}, [startDate, endDate, setSearchParams]);
	const [selectedNurse, setSelectedNurse] = useState<SystemAutoCompleteOption<Nurse> | null>(null);
	const { sortState, setSortState, sortRows } = useMultiSort<DisciplinaryReportRowData>(disciplinaryReportTableSortingKey, initialSortState);
	const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
	const [pickedRoles, setPickedRoles] = useState<NurseFullRole[] | undefined>(undefined);
	// DEFAULT: all roles until the user picks, following the list as roster roles arrive rather than copying it once.
	const selectedRoles = pickedRoles ?? departmentNurseTypes;
	const [settingsOpen, setSettingsOpen] = useState(false);
	const [dateSortOrder, setDateSortOrder] = useState<"asc" | "desc">(() => {
		if (userId) {
			return localStorageService.getDisciplinaryDateSortOrder(userId) ?? "desc";
		}
		return "desc";
	});

	useEffect(() => {
		if (userId) {
			setDateSortOrder(localStorageService.getDisciplinaryDateSortOrder(userId) ?? "desc");
		}
	}, [userId]);

	const handleDateSortOrderChange = useCallback(
		(option: { value: "asc" | "desc" }) => {
			setDateSortOrder(option.value);
			if (userId) {
				localStorageService.setDisciplinaryDateSortOrder(option.value, userId);
			}
		},
		[userId],
	);

	const allEmploymentTypes = useMemo(() => TEAM_SECTIONS.flatMap((s) => s.employmentTypes), []);
	const [selectedEmploymentTypes, setSelectedEmploymentTypes] = useState<EmploymentType[]>(allEmploymentTypes);
	const [selectedViolationTypes, setSelectedViolationTypes] = useState<DisciplinaryViolationType[]>(ALL_VIOLATION_TYPES);

	const rolesForApi = useMemo(() => {
		if (isAnyDemo || selectedRoles.length === departmentNurseTypes.length || selectedRoles.length === 0) return undefined;
		return selectedRoles;
	}, [selectedRoles, departmentNurseTypes.length, isAnyDemo]);

	const employmentTypesForApi = useMemo(() => {
		if (selectedEmploymentTypes.length === allEmploymentTypes.length || selectedEmploymentTypes.length === 0) return undefined;
		return selectedEmploymentTypes;
	}, [selectedEmploymentTypes, allEmploymentTypes.length]);

	const violationTypesForApi = useMemo(() => {
		if (selectedViolationTypes.length === ALL_VIOLATION_TYPES.length || selectedViolationTypes.length === 0) return undefined;
		return selectedViolationTypes;
	}, [selectedViolationTypes]);

	const debouncedRolesForApi = useDebouncedValue(rolesForApi, 750);
	const debouncedEmploymentTypesForApi = useDebouncedValue(employmentTypesForApi, 750);
	const { data: reportData, isLoading } = useGetDisciplinaryReport({
		startDate,
		endDate,
		roles: debouncedRolesForApi,
		employmentTypes: debouncedEmploymentTypesForApi,
		violationTypes: violationTypesForApi,
	});

	const toggleRowExpansion = (nurseId: number) => {
		setExpandedRows((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(nurseId)) {
				newSet.delete(nurseId);
			} else {
				newSet.add(nurseId);
			}
			return newSet;
		});
	};

	const collapseAll = () => {
		setExpandedRows(new Set());
	};

	const nurseOptions = useMemo<SystemAutoCompleteOption<Nurse>[]>(() => {
		// DEFAULT: no options until the roster loads; the report shows its spinner meanwhile.
		return (roster ?? [])
			.filter((nurse) => selectedRoles.includes(nurse.role) || selectedRoles.length === 0)
			.map<SystemAutoCompleteOption<Nurse>>((nurse) => ({ label: getNurseFormattedName(nurse.id), value: nurse }));
	}, [roster, getNurseFormattedName, selectedRoles]);

	const rowData = useMemo<DisciplinaryReportRowData[]>(() => {
		// Rows are named from the roster, so none are built until it loads.
		if (!reportData?.disciplinaryData || !roster) return [];

		let unsortedData = Object.entries(reportData.disciplinaryData).map<DisciplinaryReportRowData>(([nurseId, nurseData]) => ({
			nurseId: +nurseId,
			nurseName: getNurseFormattedName(+nurseId),
			violationCount: nurseData.violations.length,
			score: nurseData.summary.score,
			violations: nurseData.violations,
		}));

		if (selectedNurse) {
			unsortedData = unsortedData.filter((row) => row.nurseId === selectedNurse.value.id);
		}

		return sortRows(unsortedData);
	}, [reportData, roster, sortRows, selectedNurse, getNurseFormattedName]);

	const expandedKey = useMemo(() => [...expandedRows].sort().join(","), [expandedRows]);
	const dynamicRowHeight = useDynamicRowHeight({ defaultRowHeight: 56, key: expandedKey });

	const handleEmploymentTypeToggle = useCallback((empTypes: string[]) => {
		const relevantSectionEmploymentTypes = TEAM_SECTIONS.filter((section) => empTypes.includes(section.title)).flatMap((section) => section.employmentTypes);
		setSelectedEmploymentTypes(relevantSectionEmploymentTypes);
	}, []);

	return (
		<Box sx={{ p: 3, height: "100%", width: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
			<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
				<Typography variant="h4" sx={{ color: "primary.dark", lineHeight: 1 }}>
					Disciplinary Report
				</Typography>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<IconButton
						onClick={() => {
							setSettingsOpen(true);
						}}
						size="small"
						sx={{ color: "primary.dark" }}
					>
						<SettingsIcon />
					</IconButton>
					<DisciplinarySettingsDialog
						open={settingsOpen}
						onClose={() => {
							setSettingsOpen(false);
						}}
					/>
					{!isAnyDemo && chosenDepartment?.department_id && (
						<PushReportSchedule departmentId={chosenDepartment.department_id} reportType={PushReportType.disciplinary} />
					)}
					<DisciplinaryExportButton
						dateSortOrder={dateSortOrder}
						nurseSort={sortState}
						startDate={startDate}
						endDate={endDate}
						hasData={rowData.length > 0}
						roles={debouncedRolesForApi}
						employmentTypes={debouncedEmploymentTypesForApi}
						violationTypes={violationTypesForApi}
					/>
				</Box>
			</Box>
			<Box
				sx={{
					display: "flex",
					gap: 4,
					alignItems: "center",
					justifyContent: "space-between",
					border: "1px solid #E0E2E8",
					borderRadius: "8px",
					padding: "12px 16px",
				}}
			>
				{/* Filters Section */}
				<Box
					sx={{
						display: "flex",
						gap: 2,
						alignItems: "center",
						flexWrap: "wrap",
					}}
				>
					<DateRangeSelector
						padding={0}
						removeBackground={true}
						removeBorder={true}
						startDate={startDate}
						endDate={endDate}
						onStartDateChange={setStartDate}
						onEndDateChange={setEndDate}
						maxDate={currentDate}
					/>
					<StaffAutoComplete nurses={nurseOptions} value={selectedNurse} onChange={(_, value) => setSelectedNurse(value)} width={filtersWidth} />
					{!isAnyDemo && chosenDepartment && (
						<RoleSelector
							onRolesChange={setPickedRoles}
							chosenDepartment={chosenDepartment}
							roles={departmentNurseTypes}
							selectedRoles={selectedRoles}
							width={filtersWidth}
						/>
					)}
					<SystemDropdown
						label="Employment Type"
						multiple
						items={TEAM_SECTIONS.map((section) => ({ label: section.title, value: section.title, id: section.title }))}
						selectedItems={TEAM_SECTIONS.map((section) => section.title).filter((title) =>
							selectedEmploymentTypes.some((empType) => TEAM_SECTIONS.find((s) => s.title === title)?.employmentTypes.includes(empType)),
						)}
						onChangeMultiple={handleEmploymentTypeToggle}
						selectSx={{ width: filtersWidth }}
						renderAllValueText="All"
					/>
					<SystemDropdown
						label="Violation Type"
						multiple
						items={ALL_VIOLATION_TYPES.map((type) => ({ label: t(VIOLATION_TYPE_LABEL_KEYS[type]), value: type }))}
						selectedItems={selectedViolationTypes}
						onChangeMultiple={(values) => setSelectedViolationTypes(values as DisciplinaryViolationType[])}
						selectSx={{ width: filtersWidth }}
						renderAllValueText="All"
					/>
					<Box sx={{ display: "flex", flexDirection: "column", alignItems: "start" }}>
						<Typography sx={{ fontSize: "11px", color: "primary.dark", fontWeight: 500, lineHeight: 1, pl: 0.5 }}>Dates order</Typography>
						<SystemFlatSwitch
							options={[
								{ label: "Desc", value: "desc" as const },
								{ label: "Asc", value: "asc" as const },
							]}
							activeIndexOrValue={dateSortOrder}
							onSwitch={handleDateSortOrderChange}
						/>
					</Box>
				</Box>
				<SystemSortingButtonWithPopup<DisciplinaryReportRowData>
					sortingOptions={sortingOptions}
					initialState={sortState}
					setSortState={setSortState}
					defaultSortState={initialSortState}
				/>
			</Box>
			<DisciplinarySummaryCards rowData={rowData} />
			{isLoading || isLoadingRoster ? (
				<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
					<CircularProgress />
				</Box>
			) : isRosterError ? (
				<Box sx={{ p: 3, textAlign: "center" }}>
					<Typography variant="body2" color="error" data-testid="disciplinary-report-roster-error">
						{t("profile.roster.unavailable")}
					</Typography>
				</Box>
			) : (
				<Fragment>
					{!!reportData && (
						<Typography variant="body1" sx={{ color: "#666" }}>
							Showing data in range: {formatWeekRangeWithMonth(reportData.startDate, reportData.endDate, true, true)}
						</Typography>
					)}

					<Box
						sx={{
							display: "flex",
							width: "100%",
							justifyContent: "center",
							flex: 1,
							minHeight: 0,
						}}
					>
						<Paper
							sx={{
								display: "flex",
								flexDirection: "column",
								overflow: "hidden",
								width: {
									xs: "90%",
									xxl: "85%",
								},
							}}
						>
							{/* Table Header */}
							<Box
								sx={{
									display: "grid",
									gridTemplateColumns: gridTemplateColumns,
									gap: 2,
									px: 3,
									py: 2,
									backgroundColor: "#F5F7FA",
									borderBottom: "2px solid #E0E0E0",
									fontWeight: 600,
									alignItems: "center",
								}}
							>
								<Box id={"collapse-all-button"} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
									{expandedRows.size > 0 && (
										<Tooltip title="Collapse All" placement="top">
											<IconButton onClick={collapseAll} size="small" sx={{ color: "primary.dark" }}>
												<ChevronRightIcon sx={{ fontSize: 20, transform: "rotate(270deg)" }} />
											</IconButton>
										</Tooltip>
									)}
								</Box>
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 1,
										height: "fit-content",
									}}
								>
									<Typography variant="subtitle2" sx={{ color: "primary.dark", fontWeight: 700 }}>
										Full Name
									</Typography>
									<SortButton sortingColumnsKeys={["nurseName"]} setSortState={setSortState} sortingState={sortState} />
								</Box>
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 1,
										height: "fit-content",
										justifyContent: "center",
									}}
								>
									<Typography variant="subtitle2" sx={{ color: "primary.dark", fontWeight: 700, textAlign: "center" }}>
										Call-Ins
									</Typography>
								</Box>
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 1,
										height: "fit-content",
										justifyContent: "center",
									}}
								>
									<Typography variant="subtitle2" sx={{ color: "primary.dark", fontWeight: 700, textAlign: "center" }}>
										Schedule Violations
									</Typography>
								</Box>
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 1,
										height: "fit-content",
									}}
								>
									<Typography variant="subtitle2" sx={{ color: "primary.dark", fontWeight: 700, textAlign: "center", whiteSpace: "nowrap" }}>
										Total Disciplinary Score
									</Typography>
									<SortButton sortingColumnsKeys={["score"]} setSortState={setSortState} sortingState={sortState} />
								</Box>
							</Box>
							<Box sx={{ flex: 1, minHeight: 0 }}>
								{rowData.length > 0 ? (
									<VirtualizedList
										items={rowData}
										rowHeight={dynamicRowHeight}
										renderItem={(item) => (
											<DisciplinaryReportRow
												data={item}
												isExpanded={expandedRows.has(item.nurseId)}
												onToggleExpand={() => toggleRowExpansion(item.nurseId)}
												dateSortOrder={dateSortOrder}
											/>
										)}
										overscanCount={5}
									/>
								) : (
									<Box sx={{ p: 3, textAlign: "center" }}>
										<Typography variant="body2" sx={{ color: "#666" }}>
											No disciplinary data found for the selected date range.
										</Typography>
									</Box>
								)}
							</Box>
						</Paper>
					</Box>
				</Fragment>
			)}
		</Box>
	);
};

export default DisciplinaryReport;
